import { PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { CreateCellClickHandler, CustomCellClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { useSYS1010_R01Store } from "./Store_SYS1010_R01";
import { ShiftRangeSelectionConfig } from "src/utils/grid/useShiftRangeRowSelection";

import { defaultDetailSYS1010_R01, SYS1010_R01Dto, } from "./Dto_SYS1010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import {
	createDetailBaseColumnDefs,
	createDetailColumnDefs,
	defaultDetailColumnState,
	DETAIL_COLUMN_STATE_KEY,
} from "./ColumnsDef_SYS1010_R01";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";


/**
 * Props 타입 정의
 * @param data - 공통코드 데이터 배열
 * @param isMajor - 대분류 여부 (true: 대분류, false: 소분류)
 */
interface Props {
	data: SYS1010_R01Dto[];
	gridRef: React.RefObject<AgGridReact<SYS1010_R01Dto> | null>;
	defaultSYS1010_R01DetailRow: SYS1010_R01Dto;
}


/**
 * 공통코드 테이블 컴포넌트
 * 대분류/소분류 공통코드를 관리하는 테이블 컴포넌트
 */
export const DetailTable_SYS1010_R01 = (props: Props) => {

	const { data, gridRef, defaultSYS1010_R01DetailRow } = props;

	
	const {
		minorSYS1010_R01List, setMinorSYS1010_R01List,
		selectedMajorSYS1010_R01, setSelectedMajorSYS1010_R01, initSelectedMinorSYS1010_R01List, allSelectedMinorSYS1010_R01List,
		selectedMinorSYS1010_R01List, setSelectedMinorSYS1010_R01List, deletedMinorSYS1010_R01List,
		updateMinorSYS1010_R01List, removeMinorSYS1010_R01List, insertMinorSYS1010_R01List, removeDeletedMinorSYS1010_R01List,
		addDeletedMinorSYS1010_R01List,
		originalMinorSYS1010_R01List, setOriginalMinorSYS1010_R01List, 
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useSYS1010_R01Store();

	// ag-Grid 참조 및 상태 관리
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const [clickable, setClickable] = useState(true);
	const [summaryWidths, setSummaryWidths] = useState<number[]>([]);

	const { 
		getRowId,
		paddedGridData,
		renderIndexCell,
		createRowClassRules,
		createCellClassRules,
		defaultRowIndexCellButton,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: minorSYS1010_R01List,
		defaultGridData: defaultDetailSYS1010_R01,
		selectedRow: selectedMinorSYS1010_R01List,
		originalGridData: originalMinorSYS1010_R01List,
		updateRow: (index: number, data: any) => updateMinorSYS1010_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: ['minorCd'],
		pointerCells: ['codeNm', 'sysFlg', 'dispSeq', 'remark', 'ref01', 'ref02', 'ref03', 'ref04', 'ref05', 'ref06', 'ref07', 'ref08', 'ref09', 'ref10',],
		insertedPointerCells: ['minorCd'],
		commonPointerCells: [''],
		alignLeftCells: ['minorCd','ref01', 'ref02', 'ref03', 'ref04', 'ref05', 'ref06', 'ref07', 'ref08', 'ref09', 'ref10',]
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SYS1010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedMinorSYS1010_R01List,
			deletedList: deletedMinorSYS1010_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [selectedMinorSYS1010_R01List, deletedMinorSYS1010_R01List, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	const commonInputColumns = ['majorCd', 'minorCd', 'codeNm', 'sysFlg', 'dispSeq', 'remark', 'ref01', 'ref02', 'ref03', 'ref04', 'ref05', 'ref06', 'ref07', 'ref08', 'ref09', 'ref10'];

	const defaultEditableColumns = ['minorCd', 'codeNm', 'dispSeq', 'remark', 'ref01', 'ref02', 'ref03', 'ref04', 'ref05', 'ref06', 'ref07', 'ref08', 'ref09', 'ref10'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {	
		InsertRowHandler({
			event: e,
			dataList: minorSYS1010_R01List,
			rowData: minorSYS1010_R01List[rowIndex],
			insertRow: insertMinorSYS1010_R01List,
			defaultRowData: defaultDetailSYS1010_R01,
			setClickable
		});
	}, [minorSYS1010_R01List, insertMinorSYS1010_R01List, defaultDetailSYS1010_R01, setClickable]);

	const indexCellRenderer = useMemo(() => (params: SYS1010_R01Dto) =>
		renderIndexCell(
			params,
			(innerParams: SYS1010_R01Dto) => getRowStyleFlags(
				innerParams,
				{
					deletedList: deletedMinorSYS1010_R01List,
					selectedList: selectedMinorSYS1010_R01List,
					dataList: minorSYS1010_R01List
				}),
			(rowIndex: number, e: React.MouseEvent) => {
				if (e.ctrlKey) {
					for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
				} else {
					insertRowHandler(rowIndex, e);
				}
			},
			(rowData) => RemoveRowHandler(
				gridRef,
				minorSYS1010_R01List,
				rowData,
				setClickable,
				removeMinorSYS1010_R01List,
				(valueIndex) => addDeletedMinorSYS1010_R01List([valueIndex.toString()])
			),
		)
	, [renderIndexCell, deletedMinorSYS1010_R01List, selectedMinorSYS1010_R01List, minorSYS1010_R01List, insertRowHandler, gridRef, setClickable, removeMinorSYS1010_R01List, addDeletedMinorSYS1010_R01List]);

	const baseColumnDefs = useMemo(
		() => createDetailBaseColumnDefs({ indexCellRenderer, handleSwitchButtonHandler }),
	[indexCellRenderer, handleSwitchButtonHandler]);

	const columnDefs = useMemo(
		() => createDetailColumnDefs(baseColumnDefs, detailColumnState),
	[baseColumnDefs, detailColumnState]);

	const isSelectableRow = useCallback((valueIndex: number | null | undefined) => (
		valueIndex !== undefined &&
		valueIndex !== null &&
		valueIndex !== 0 &&
		!deletedMinorSYS1010_R01List.includes(String(valueIndex))
	), [deletedMinorSYS1010_R01List]);

	const shiftRangeSelectionConfig: ShiftRangeSelectionConfig<SYS1010_R01Dto> = {
		enableShiftRangeSelect: true,
		idKey: 'valueIndex',
		selectedIds: selectedMinorSYS1010_R01List as unknown as Array<string | number>,
		setSelectedIds: (ids) => setSelectedMinorSYS1010_R01List(ids as any),
		isRowSelectable: (event) => {
			if (!clickable) {
				setClickable(true);
				return false;
			}
			return isSelectableRow(event.data?.valueIndex);
		},
	};

	const cellEventHandlers = useDetailTableEvents({
		detailList: minorSYS1010_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<SYS1010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(minorSYS1010_R01List, 'valueIndex', cellClickActions);


	const handleCellChange = useCellChangeHandler({
		dataList: minorSYS1010_R01List,
		originalDataList: originalMinorSYS1010_R01List,
		updateRow: updateMinorSYS1010_R01List,
		customBeforeUpdate: (updatedData, field, newValue) => {
			if (field === 'minorCd' && typeof newValue === 'string') {
				return { ...updatedData, minorCd: newValue.toUpperCase() };
			}
			return updatedData;
		},
	});

	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS1010_R01Dto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, minorSYS1010_R01List, originalMinorSYS1010_R01List, setMinorSYS1010_R01List);
	}, [minorSYS1010_R01List, originalMinorSYS1010_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		majorCd: `${paddedGridData.length.toLocaleString()}건`,
		minorCd: `${paddedGridData.length.toLocaleString()}건`,
		editable: false,
	}]), [paddedGridData.length]);

	// 렌더링
	return (
		<GridContextWrapper<SYS1010_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setMinorSYS1010_R01List}
			initSelected={initSelectedMinorSYS1010_R01List}
			defaultRowData={defaultSYS1010_R01DetailRow}
			allSelected={allSelectedMinorSYS1010_R01List}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultDetailColumnState(baseColumnDefs)}
			setColumnState={setDetailColumnState}
			localStorageKey={DETAIL_COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SYS1010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<SYS1010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedMinorSYS1010_R01List,
					deletedList: deletedMinorSYS1010_R01List
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellValueChanged={handleCellChange}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				onRowDragEnter={() => { isRowDraggingRef.current = true; }}
				onRowDragEnd={() => { isRowDraggingRef.current = false; }}

				columnStateKey={DETAIL_COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultDetailColumnState(baseColumnDefs)}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
} 
